<script setup lang="ts">
type PaginationItem = number | 'ellipsis-left' | 'ellipsis-right'

const LEFT_ARROW_ICON = 'https://resources1.interface003.com/web30/2026/06/16/d7b229c1-d4e6-41b3-8268-297e0ff96f0f.png'
const RIGHT_ARROW_ICON = 'https://resources1.interface003.com/web30/2026/06/16/86cc7718-9d72-47eb-a017-d6c6d401510c.png'
const MAX_VISIBLE_PAGE_BUTTONS = 7
const PAGE_ROUTE_PLACEHOLDER = '{idx}'
const FIRST_PAGE_ROUTE_PATTERN = /\/page\/\{idx\}$/

const { locale } = useAppI18n()
const route = useRoute()

const props = withDefaults(
  defineProps<{
    currentPage?: number
    total?: number
    pageSize?: number
    pageRoute?: string
  }>(),
  {
    currentPage: 1,
    total: 0,
    pageSize: 0,
    pageRoute: '',
  },
)

/** 统一兜底当前页，避免传入非法页码。 */
const safeCurrentPage = computed(() => {
  const page = Math.trunc(props.currentPage)

  if (!Number.isFinite(page) || page < 1) {
    return 1
  }

  return page
})

/** 统一兜底总页数，至少保留一页。 */
const safeMaxPage = computed(() => {
  if (props.total <= 0 || props.pageSize <= 0) {
    return 1
  }

  return Math.max(1, Math.ceil(props.total / props.pageSize))
})

/** 上一页按钮是否可点击，由当前页码自动推导。 */
const canGoPrevComputed = computed(() => safeCurrentPage.value > 1)

/** 下一页按钮是否可点击，由当前页码自动推导。 */
const canGoNextComputed = computed(() => safeCurrentPage.value < safeMaxPage.value)

/** 按 Figma 的展示规则生成页码与省略号。 */
const paginationItems = computed<PaginationItem[]>(() => {
  const currentPage = safeCurrentPage.value
  const maxPage = safeMaxPage.value

  if (maxPage <= MAX_VISIBLE_PAGE_BUTTONS) {
    return Array.from({ length: maxPage }, (_, index) => index + 1)
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-right', maxPage]
  }

  if (currentPage >= maxPage - 3) {
    return [1, 'ellipsis-left', maxPage - 4, maxPage - 3, maxPage - 2, maxPage - 1, maxPage]
  }

  return [1, 'ellipsis-left', currentPage - 1, currentPage, currentPage + 1, 'ellipsis-right', maxPage]
})

/** 生成分页跳转链接，自动补齐当前语言前缀。 */
const buildPageRoute = (page: number) => {
  if (!props.pageRoute.includes(PAGE_ROUTE_PLACEHOLDER) || page < 1 || page > safeMaxPage.value) {
    return route.fullPath
  }

  const pageRoute =
    page === 1 && FIRST_PAGE_ROUTE_PATTERN.test(props.pageRoute)
      ? props.pageRoute.replace(FIRST_PAGE_ROUTE_PATTERN, '')
      : props.pageRoute.replace(PAGE_ROUTE_PLACEHOLDER, `${page}`)
  const normalizedPageRoute = pageRoute.startsWith('/') ? pageRoute : `/${pageRoute}`
  const currentLocale = `${locale || ''}`.replace(/^\/+|\/+$/g, '')

  if (!currentLocale || currentLocale === 'en') {
    return normalizedPageRoute
  }

  return `/${currentLocale}${normalizedPageRoute}`
}
</script>

<template>
  <div v-if="safeMaxPage > 1" class="rtl:gap-x-reverse inline-flex items-center justify-start gap-x-[16px]">
    <NuxtLink
      v-if="canGoPrevComputed"
      :to="buildPageRoute(safeCurrentPage - 1)"
      class="flex size-[44px] shrink-0 cursor-pointer items-center justify-center rounded-full p-[10px] hover:bg-[#F8F8F8] rtl:rotate-180"
      aria-label="Previous page"
    >
      <CommonImage class="size-[20px]" :src="LEFT_ARROW_ICON" alt="" />
    </NuxtLink>

    <button
      v-else
      type="button"
      class="flex size-[44px] shrink-0 items-center justify-center rounded-full p-[10px] opacity-30 rtl:rotate-180"
      disabled
      aria-label="Previous page"
    >
      <CommonImage class="size-[20px]" :src="LEFT_ARROW_ICON" alt="" />
    </button>

    <template v-for="item in paginationItems" :key="`${item}`">
      <button
        v-if="typeof item === 'number' && item === safeCurrentPage"
        type="button"
        class="flex size-[44px] shrink-0 items-center justify-center rounded-full bg-[#F8F8F8] p-[10px] text-right text-[18px] leading-[26px] font-medium text-[#292A2E]"
        :aria-current="item === safeCurrentPage ? 'page' : undefined"
      >
        {{ item }}
      </button>

      <NuxtLink
        v-else-if="typeof item === 'number'"
        :to="buildPageRoute(item)"
        class="flex size-[44px] shrink-0 cursor-pointer items-center justify-center rounded-full p-[10px] text-right text-[18px] leading-[26px] font-medium text-[#292A2E] hover:bg-[#F8F8F8]"
      >
        {{ item }}
      </NuxtLink>

      <div
        v-else
        class="flex size-[44px] shrink-0 items-center justify-center rounded-full p-[10px] text-right font-['PingFang_SC'] text-[18px] leading-[26px] font-medium text-[#292A2E]"
      >
        ...
      </div>
    </template>

    <NuxtLink
      v-if="canGoNextComputed"
      :to="buildPageRoute(safeCurrentPage + 1)"
      class="flex size-[44px] shrink-0 cursor-pointer items-center justify-center rounded-full p-[10px] hover:bg-[#F8F8F8] rtl:rotate-180"
      aria-label="Next page"
    >
      <CommonImage class="size-[20px]" :src="RIGHT_ARROW_ICON" alt="" />
    </NuxtLink>

    <button
      v-else
      type="button"
      class="flex size-[44px] shrink-0 items-center justify-center rounded-full p-[10px] opacity-30 rtl:rotate-180"
      disabled
      aria-label="Next page"
    >
      <CommonImage class="size-[20px]" :src="RIGHT_ARROW_ICON" alt="" />
    </button>
  </div>
</template>

<style scoped></style>
